/** /routines - inferred recurring routines with cadence. Dotted
 *  until confirmed. "Log performed now" records last_performed_at. */
import { useCallback, useEffect, useState } from "react";
import { Repeat, Check, X, CircleCheck } from "lucide-react";
import { api } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

type Routine = Record<string, unknown>;

export function Routines() {
  const [items, setItems] = useState<Routine[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api.routinesList().then((d) => setItems(d.routines || [])).catch(() => undefined);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id: string, action: string) => {
    setBusy(id);
    try {
      await api.lifestyleAction("routines", id, action);
      if (action === "reject") setItems((c) => c.filter((r) => r.id !== id));
      else refresh();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-4">
      <Breadcrumb segments={[{ label: "Explore", href: "/explore" }, { label: "Library", href: "/explore/library" }, { label: "Routines" }]} />
      <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Routines</h1>
      <TeachingBanner id="routines_overview" />
      {items.length === 0 && <p className="text-sm text-slate-500">No routines detected yet.</p>}
      <ul className="space-y-2">
        {items.map((r) => {
          const id = String(r.id);
          const confirmed = Number(r.confirmed) === 1;
          return (
            <li
              key={id}
              className={`rounded-xl p-3 border ${
                confirmed
                  ? "border-slate-200/70 dark:border-slate-700/50"
                  : "border-dashed border-slate-400 dark:border-slate-600"
              } bg-white/60 dark:bg-slate-900/40`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-sky-500" />
                    <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{String(r.title)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{String(r.kind)}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    {String(r.cadence_rule || "no cadence")}
                    {r.last_performed_at ? ` · last ${new Date(String(r.last_performed_at)).toLocaleDateString()}` : " · never logged"}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {!confirmed && (
                    <>
                      <button type="button" disabled={busy === id} onClick={() => act(id, "confirm")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Confirm</button>
                      <button type="button" disabled={busy === id} onClick={() => act(id, "reject")} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><X className="w-3.5 h-3.5" /> Reject</button>
                    </>
                  )}
                  <button type="button" disabled={busy === id} onClick={() => act(id, "log-performed")} className="inline-flex items-center gap-1 rounded-lg bg-sky-600 text-white text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><CircleCheck className="w-3.5 h-3.5" /> Log performed</button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
